// @ts-nocheck
/**
 * API SERVICE LAYER — Jambushrushti Jain Library
 *
 * ━━━ HOW TO SWITCH FROM DEMO TO REAL BACKEND ━━━
 *
 * 1. Set VITE_API_BASE_URL in your .env.local:
 *    VITE_API_BASE_URL=http://localhost:8000/api
 *
 * 2. In each function below:
 *    - Comment out the `return seed...` line
 *    - Uncomment the `const res = await fetch(...)` block
 */

import type {
  Book,
  Manuscript,
  Member,
  BookIssue,
  Approval,
  Ticket,
  StorageLocation,
  LibrarySettings,
  DashboardStats,
  CirculationData,
  ActivityItem,
  FineItem,
} from "@libraryadmin/data/seed";
import {
  getLibraryAdminSession,
  refreshLibraryAdminToken,
} from "@libraryadmin/services/auth";

// Empty in-memory stores. Records created during the current session are kept
// here until the backend API is connected.
const seedBooks: Book[] = [];
const seedManuscripts: Manuscript[] = [];
const seedMembers: Member[] = [];
const seedBookIssues: BookIssue[] = [];
const seedApprovals: Approval[] = [];
const seedTickets: Ticket[] = [];
const seedStorageLocations: StorageLocation[] = [];
const seedFines: FineItem[] = [];

const seedSettings: LibrarySettings = {
  libraryName: "",
  phone: "",
  email: "",
  address: "",
  workingHours: "",
  maxBooksPerMember: 0,
  issueDurationDays: 0,
  finePerDay: 0,
  fineRateTier1: 0,
  fineRateTier2: 0,
  fineRateTier3: 0,
  fineMaxCap: 0,
  fineGracePeriodDays: 0,
  lateReturnRuleActive: false,
  lostBookProcessingFee: 0,
  lostBookMrpMultiplier: 0,
  lostBookFlatFee: 0,
  lostBookRuleActive: false,
  damagedBookMinorFee: 0,
  damagedBookModerateFee: 0,
  damagedBookSevereFee: 0,
  damagedBookRuleActive: false,
  annualMembershipFee: 0,
  membershipExpiryRuleActive: false,
  ruleAppliedDates: {},
  restrictedAgeThreshold: 0,
  autoApproveMembers: false,
};

const API_BASE = import.meta.env.DEV
  ? "/jambushrusti-api"
  : (import.meta.env.VITE_API_BASE_URL || "").replace(/\/$/, "");
const TENANT_ID = import.meta.env.VITE_X_TENANTID;

// Simulated network delay for realistic demo feel
const delay = (ms = 300) => new Promise(res => setTimeout(res, ms));

// ─── DASHBOARD ────────────────────────────────────────────────────────────────

export async function getDashboardStats(): Promise<DashboardStats> {
  const { books, manuscripts, members, issues, returns } =
    await getDashboardSnapshot();
  const currentMonth = new Date().toISOString().slice(0, 7);
  return {
    totalBooks: books.length,
    totalManuscripts: manuscripts.length,
    activeMembers: members.filter(member => member.status === "active").length,
    overdueBooks: issues.filter(issue => issue.status === "overdue").length,
    booksIssuedThisMonth: issues.filter(issue =>
      issue.issueDate.startsWith(currentMonth)
    ).length,
    booksReturnedThisMonth: returns.filter(item =>
      item.returnDate.startsWith(currentMonth)
    ).length,
    newMembersThisMonth: members.filter(member =>
      member.joinedDate.startsWith(currentMonth)
    ).length,
  };
}

export async function getCirculationData(): Promise<CirculationData[]> {
  const { issues, returns } = await getDashboardSnapshot();
  const months = Array.from({ length: 6 }, (_, index) => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() - (5 - index));
    return {
      key: date.toISOString().slice(0, 7),
      label: date.toLocaleDateString("en-IN", { month: "short" }),
    };
  });

  return months.map(({ key, label }) => ({
    month: label,
    issued: issues.filter(issue => issue.issueDate.startsWith(key)).length,
    received: returns.filter(item => item.returnDate.startsWith(key)).length,
    overdue: issues.filter(
      issue => issue.status === "overdue" && issue.dueDate.startsWith(key)
    ).length,
  }));
}

export async function getRecentActivity(): Promise<ActivityItem[]> {
  const { members, issues, returns } = await getDashboardSnapshot();
  const activities: Array<ActivityItem & { sortDate: string }> = [
    ...issues.map(issue => ({
      id: `issue-${issue.id}`,
      type: "issued" as const,
      title: `Book issued to ${issue.memberName}`,
      description: `${issue.bookTitle || issue.bookNo} · ${issue.issueNo}`,
      timestamp: formatActivityDate(issue.issueDate),
      sortDate: issue.issueDate,
      icon: "book_issued" as const,
    })),
    ...returns.map(item => ({
      id: `return-${item.id}`,
      type: "returned" as const,
      title: `Book returned by ${item.memberName}`,
      description: `${item.bookName || item.bookNo} · ${item.returnNo}`,
      timestamp: formatActivityDate(item.returnDate),
      sortDate: item.returnDate,
      icon: "book_returned" as const,
    })),
    ...members.map(member => ({
      id: `member-${member.id}`,
      type: "new_member" as const,
      title: `New member: ${member.name}`,
      description: `${member.memberId}${member.city ? ` · ${member.city}` : ""}`,
      timestamp: formatActivityDate(member.joinedDate),
      sortDate: member.joinedDate,
      icon: "member" as const,
    })),
  ];

  return activities
    .filter(activity => activity.sortDate)
    .sort((a, b) => b.sortDate.localeCompare(a.sortDate))
    .slice(0, 6)
    .map(({ sortDate: _sortDate, ...activity }) => activity);
}

export async function getCollectionOverview() {
  const { books, manuscripts } = await getDashboardSnapshot();
  const bookTypes = books.reduce<Record<string, number>>((counts, book) => {
    const name = book.type?.trim() || "Books";
    counts[name] = (counts[name] || 0) + 1;
    return counts;
  }, {});

  const collection = Object.entries(bookTypes).map(([name, value]) => ({
    name,
    value,
  }));
  if (manuscripts.length) {
    collection.push({ name: "Manuscripts", value: manuscripts.length });
  }
  return collection;
}

type DashboardSnapshot = {
  books: Book[];
  manuscripts: Manuscript[];
  members: Member[];
  issues: BookIssue[];
  returns: BookReturnRecord[];
};

let dashboardSnapshot: DashboardSnapshot | null = null;
let dashboardSnapshotAt = 0;
let dashboardSnapshotRequest: Promise<DashboardSnapshot> | null = null;

async function getDashboardSnapshot(): Promise<DashboardSnapshot> {
  if (dashboardSnapshot && Date.now() - dashboardSnapshotAt < 1_000) {
    return dashboardSnapshot;
  }
  if (dashboardSnapshotRequest) {
    return dashboardSnapshotRequest;
  }

  dashboardSnapshotRequest = (async () => {
    const [books, manuscripts, members, issues, returns] = await Promise.all([
      getBooks(),
      getManuscripts(),
      getMembers(),
      getBookIssues(),
      getBookReturns(),
    ]);
    dashboardSnapshot = { books, manuscripts, members, issues, returns };
    dashboardSnapshotAt = Date.now();
    return dashboardSnapshot;
  })();

  try {
    return await dashboardSnapshotRequest;
  } finally {
    dashboardSnapshotRequest = null;
  }
}

function formatActivityDate(value: string): string {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
  });
}

// ─── BOOKS ────────────────────────────────────────────────────────────────────

const BOOKS_PATH = "/jambushrusti/library/books/";

type ApiBook = Record<string, any>;

function getBookApiError(payload: any, fallback: string): string {
  if (typeof payload === "string" && payload) {
    return payload;
  }
  if (typeof payload?.detail === "string") {
    return payload.detail;
  }
  if (typeof payload?.message === "string") {
    return payload.message;
  }
  if (typeof payload?.error === "string") {
    return payload.error;
  }
  if (typeof payload?.error?.message === "string") {
    return payload.error.message;
  }

  if (payload && typeof payload === "object") {
    const firstError = Object.values(payload).flat().find(Boolean);
    if (typeof firstError === "string") {
      return firstError;
    }
  }

  return fallback;
}

async function requestBooksApi(
  path: string,
  options: RequestInit = {}
): Promise<any> {
  let accessToken = getLibraryAdminSession()?.accessToken;

  if (!API_BASE || !TENANT_ID) {
    throw new Error("The library books API is not configured.");
  }
  if (!accessToken) {
    throw new Error("Your session has expired. Please sign in again.");
  }

  const sendRequest = (token: string) =>
    fetch(`${API_BASE}${BOOKS_PATH}${path}`, {
      ...options,
      headers: {
        ...(options.body instanceof FormData
          ? {}
          : { "Content-Type": "application/json" }),
        Authorization: `Bearer ${token}`,
        "X-Tenant-ID": TENANT_ID,
        ...options.headers,
      },
    });

  let response = await sendRequest(accessToken);
  if (response.status === 401) {
    accessToken = await refreshLibraryAdminToken();
    response = await sendRequest(accessToken);
  }

  if (response.status === 204) {
    return undefined;
  }

  const payload = await response.json().catch(() => undefined);
  if (!response.ok) {
    throw new Error(
      getBookApiError(payload, `Book request failed (${response.status}).`)
    );
  }
  return payload;
}

function unwrapApiData(payload: any): ApiBook {
  let value = payload;
  while (
    value &&
    !Array.isArray(value) &&
    typeof value === "object" &&
    (value.data !== undefined || value.result !== undefined)
  ) {
    value = value.data ?? value.result;
  }
  return value || {};
}

function extractBookList(payload: any): ApiBook[] {
  let value = payload;
  for (let depth = 0; depth < 4; depth += 1) {
    if (Array.isArray(value)) {
      return value;
    }
    if (!value || typeof value !== "object") {
      return [];
    }
    value = value.results ?? value.data ?? value.items ?? value.books;
  }
  return Array.isArray(value) ? value : [];
}

function normalizeBookStatus(status: unknown): Book["status"] {
  const normalized = String(status || "available").toLowerCase();
  return ["available", "issued", "reserved"].includes(normalized)
    ? normalized
    : "available";
}

function mapApiBook(book: ApiBook): Book {
  const rack = String(book.rack_no ?? book.rack ?? "");
  const bin = String(book.bin_no ?? book.bin ?? "");
  const cupboard = String(book.cupboard_no ?? book.cupboard ?? "");
  const location = [rack && `Rack ${rack}`, bin, cupboard]
    .filter(Boolean)
    .join(" / ");

  return {
    id: String(book.id ?? book.uuid ?? book.book_id ?? book.book_no ?? ""),
    bookNo: String(book.book_no ?? book.bookNo ?? ""),
    title: String(book.main_title ?? book.title ?? ""),
    aliasName: book.alias_name ?? book.aliasName ?? "",
    type: book.book_type ?? book.type ?? "",
    topic: book.topic ?? "",
    author: String(book.primary_author ?? book.author ?? ""),
    tikakar: book.tikakar ?? "",
    translator: book.translator ?? "",
    publisher: String(book.publisher ?? ""),
    year: String(book.publication_year ?? book.year ?? ""),
    isbn: book.isbn ?? "",
    pages: Number(book.pages_count ?? book.pages ?? 0),
    price:
      book.price === null || book.price === undefined
        ? null
        : Number(book.price),
    pdfLink: book.digital_pdf_url ?? book.pdfLink ?? "",
    condition: book.condition ?? "Good",
    rack,
    bin,
    cupboard,
    language: book.language ?? "Gujarati",
    remarks: book.remarks ?? "",
    location: book.location ?? location,
    status: normalizeBookStatus(book.status),
  };
}

function mapBookToApi(book: Partial<Book>) {
  const publicationYear = Number(book.year);
  return {
    book_no: book.bookNo,
    main_title: book.title,
    alias_name: book.aliasName || "",
    book_type: book.type || "",
    topic: book.topic || "",
    primary_author: book.author || "",
    tikakar: book.tikakar || "",
    translator: book.translator || "",
    publisher: book.publisher || "",
    publication_year: Number.isFinite(publicationYear) ? publicationYear : null,
    isbn: book.isbn || "",
    language: book.language,
    pages_count: Number(book.pages) || 0,
    price:
      book.price === null || book.price === undefined
        ? null
        : Number(book.price),
    condition: book.condition,
    status: book.status
      ? `${book.status.charAt(0).toUpperCase()}${book.status.slice(1)}`
      : undefined,
    digital_pdf_url: book.pdfLink || "",
    rack_no: book.rack || "",
    bin_no: book.bin || "",
    cupboard_no: book.cupboard || "",
    remarks: book.remarks || "",
    location: book.location || "",
  };
}

export interface BookImportError {
  row: number;
  field: string;
  message: string;
}

export interface BookImportResult {
  total_rows: number;
  created: number;
  updated: number;
  skipped: number;
  failed: number;
  errors: BookImportError[];
}

export async function bulkImportBooks(
  file: File,
  duplicateMode: "skip" | "update" = "skip"
): Promise<BookImportResult> {
  const body = new FormData();
  body.append("file", file);
  body.append("duplicate_mode", duplicateMode);
  return requestBooksApi("bulk-import/", { method: "POST", body });
}

export async function getBooks(
  onPageLoaded?: (books: Book[]) => void
): Promise<Book[]> {
  const all: Book[] = [];
  let page = 1;
  let hasNext = true;
  while (hasNext) {
    const payload = await requestBooksApi(`?page=${page}`);
    all.push(...extractBookList(payload).map(mapApiBook));
    onPageLoaded?.([...all]);
    hasNext = Boolean(payload?.links?.next ?? payload?.next);
    page += 1;
  }
  return all;
}

export async function getBook(id: string): Promise<Book | undefined> {
  const payload = await requestBooksApi(`${encodeURIComponent(id)}/`);
  return mapApiBook(unwrapApiData(payload));
}

export async function createBook(data: Omit<Book, "id">): Promise<Book> {
  const payload = await requestBooksApi("", {
    method: "POST",
    body: JSON.stringify(mapBookToApi(data)),
  });
  return mapApiBook(unwrapApiData(payload));
}

export async function updateBook(
  id: string,
  data: Partial<Book>
): Promise<Book> {
  const payload = await requestBooksApi(`${encodeURIComponent(id)}/`, {
    method: "PATCH",
    body: JSON.stringify(mapBookToApi(data)),
  });
  return mapApiBook(unwrapApiData(payload));
}

export async function deleteBook(id: string): Promise<void> {
  await requestBooksApi(`${encodeURIComponent(id)}/`, { method: "DELETE" });
}

// ─── MANUSCRIPTS ──────────────────────────────────────────────────────────────

const MANUSCRIPTS_PATH = "/jambushrusti/library/manuscripts/";

async function requestManuscriptsApi(path: string, options: RequestInit = {}) {
  let accessToken = getLibraryAdminSession()?.accessToken;
  if (!API_BASE || !TENANT_ID) {
    throw new Error("The manuscripts API is not configured.");
  }
  if (!accessToken) {
    throw new Error("Your session has expired. Please sign in again.");
  }

  const sendRequest = (token: string) =>
    fetch(`${API_BASE}${MANUSCRIPTS_PATH}${path}`, {
      ...options,
      headers: {
        ...(options.body instanceof FormData
          ? {}
          : { "Content-Type": "application/json" }),
        Authorization: `Bearer ${token}`,
        "X-Tenant-ID": TENANT_ID,
        ...options.headers,
      },
    });

  let response = await sendRequest(accessToken);
  if (response.status === 401) {
    accessToken = await refreshLibraryAdminToken();
    response = await sendRequest(accessToken);
  }
  if (response.status === 204) {
    return undefined;
  }

  const payload = await response.json().catch(() => undefined);
  if (!response.ok) {
    throw new Error(
      getBookApiError(
        payload,
        `Manuscript request failed (${response.status}).`
      )
    );
  }
  return payload;
}

function extractManuscriptList(payload: any): Record<string, any>[] {
  let value = payload;
  for (let depth = 0; depth < 5; depth += 1) {
    if (Array.isArray(value)) {
      return value;
    }
    if (!value || typeof value !== "object") {
      return [];
    }
    value =
      value.results ??
      value.data ??
      value.items ??
      value.manuscripts ??
      value.result;
  }
  return Array.isArray(value) ? value : [];
}

function mapApiManuscript(ms: Record<string, any>): Manuscript {
  const status = String(ms.status ?? "approved").toLowerCase();
  return {
    id: String(ms.id ?? ms.uuid ?? ms.manuscript_id ?? ms.hastrap_ms_no ?? ""),
    manuscriptNo: String(ms.hastrap_ms_no ?? ms.manuscriptNo ?? ""),
    title: String(ms.granth_name ?? ms.title ?? ""),
    author: String(ms.mulkarta_author ?? ms.author ?? ""),
    mulkarta: String(ms.mulkarta_author ?? ms.mulkarta ?? ""),
    tikakarta: String(ms.tikakarta ?? ""),
    era: String(ms.era_vikram_samvat ?? ms.era ?? ""),
    script: String(ms.script ?? ms.manuscript_type ?? ""),
    language: String(ms.language ?? ""),
    pages: Number(ms.total_pages ?? ms.pages ?? 0),
    availablePages: Number(ms.available_pages ?? ms.availablePages ?? 0),
    condition: ms.condition ?? "Good",
    location: String(ms.location ?? ""),
    digitalised: Boolean(ms.is_digitised ?? ms.digitalised),
    category: String(ms.category ?? ""),
    restricted: Boolean(ms.is_restricted_access ?? ms.restricted),
    completionStatus: ms.completion_status ?? ms.completionStatus,
    status: ["approved", "pending", "rejected"].includes(status)
      ? status
      : "approved",
    remarks: String(ms.remarks ?? ""),
    imageUrl: String(ms.image ?? ms.image_url ?? ""),
  };
}

function mapManuscriptToApi(ms: Partial<Manuscript> & Record<string, any>) {
  const writingYear = Number(ms.year ?? ms.writingYear);
  const rawCompletionStatus = ms.complete ?? ms.completionStatus;
  const completionStatus =
    {
      yes: "Complete",
      no: "Incomplete",
      complete: "Complete",
      incomplete: "Incomplete",
      partial: "Partial",
    }[String(rawCompletionStatus || "").toLowerCase()] || rawCompletionStatus;
  const payload: Record<string, unknown> = {
    hastrap_ms_no: ms.manuscriptNo,
    granth_name: ms.title,
    mulkarta_author: ms.mulkarta || ms.author || "",
    tikakarta: ms.tikakarta || "",
    era_vikram_samvat: ms.era || "",
    language: ms.language || "",
    category: ms.categories?.join(", ") || ms.category || "",
    manuscript_type: ms.types?.join(", ") || ms.script || "",
    condition: ms.condition,
    total_pages: Number(ms.pages) || 0,
    available_pages: Number(ms.availablePages) || 0,
    writing_year: Number.isFinite(writingYear) ? writingYear : null,
    topic: ms.topic || "",
    pdf_link: ms.pdfLink || "",
    is_digitised: Boolean(ms.digitalised),
    is_restricted_access: Boolean(ms.restricted),
    remarks: ms.remarks || "",
  };
  if (completionStatus) {
    payload.completion_status = completionStatus;
  }
  return payload;
}

function mapManuscriptToMultipart(
  ms: Partial<Manuscript> & Record<string, any>
) {
  const values = mapManuscriptToApi(ms);
  const body = new FormData();
  Object.entries(values).forEach(([key, value]) => {
    if (value !== null && value !== undefined) {
      body.append(key, String(value));
    }
  });
  if (ms.imageFile instanceof File) {
    body.append("image", ms.imageFile);
  }
  return body;
}

export async function getManuscripts(): Promise<Manuscript[]> {
  const all: Manuscript[] = [];
  let page = 1;
  let hasNext = true;
  while (hasNext) {
    const payload = await requestManuscriptsApi(`?page=${page}`);
    all.push(...extractManuscriptList(payload).map(mapApiManuscript));
    hasNext = Boolean(payload?.links?.next ?? payload?.next);
    page += 1;
  }
  return all;
}

export async function getManuscript(
  id: string
): Promise<Manuscript | undefined> {
  const payload = await requestManuscriptsApi(`${encodeURIComponent(id)}/`);
  return mapApiManuscript(unwrapApiData(payload));
}

export async function createManuscript(
  data: Omit<Manuscript, "id" | "status">
): Promise<Manuscript> {
  const payload = await requestManuscriptsApi("", {
    method: "POST",
    body: mapManuscriptToMultipart(data),
  });
  return mapApiManuscript(unwrapApiData(payload));
}

export async function updateManuscript(
  id: string,
  data: Partial<Manuscript>
): Promise<Manuscript> {
  const payload = await requestManuscriptsApi(`${encodeURIComponent(id)}/`, {
    method: "PATCH",
    body: mapManuscriptToMultipart(data),
  });
  return mapApiManuscript(unwrapApiData(payload));
}

export async function deleteManuscript(id: string): Promise<void> {
  await requestManuscriptsApi(`${encodeURIComponent(id)}/`, {
    method: "DELETE",
  });
}

// ─── STORAGE LOCATIONS ────────────────────────────────────────────────────────

const STORAGE_LOCATIONS_PATH = "/jambushrusti/library/storage-locations/";

async function requestStorageLocationsApi(
  path: string,
  options: RequestInit = {}
): Promise<any> {
  let accessToken = getLibraryAdminSession()?.accessToken;
  if (!API_BASE || !TENANT_ID) {
    throw new Error("The storage-locations API is not configured.");
  }
  if (!accessToken) {
    throw new Error("Your session has expired. Please sign in again.");
  }

  const sendRequest = (token: string) =>
    fetch(`${API_BASE}${STORAGE_LOCATIONS_PATH}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Tenant-ID": TENANT_ID,
        ...options.headers,
      },
    });

  let response = await sendRequest(accessToken);
  if (response.status === 401) {
    accessToken = await refreshLibraryAdminToken();
    response = await sendRequest(accessToken);
  }
  if (response.status === 204) {
    return undefined;
  }

  const payload = await response.json().catch(() => undefined);
  if (!response.ok) {
    throw new Error(
      getBookApiError(
        payload,
        `Storage location request failed (${response.status}).`
      )
    );
  }
  return payload;
}

function extractStorageLocationList(payload: any): Record<string, any>[] {
  let value = payload;
  for (let depth = 0; depth < 4; depth += 1) {
    if (Array.isArray(value)) {
      return value;
    }
    if (!value || typeof value !== "object") {
      return [];
    }
    value =
      value.results ?? value.data ?? value.items ?? value.storage_locations;
  }
  return Array.isArray(value) ? value : [];
}

function normalizeStorageType(value: unknown): StorageLocation["type"] {
  const type = String(value || "rack").toLowerCase();
  return ["vault", "rack", "cupboard", "shelf", "other"].includes(type)
    ? type
    : "other";
}

function mapApiStorageLocation(location: Record<string, any>): StorageLocation {
  return {
    id: String(location.id ?? location.uuid ?? location.location_code ?? ""),
    code: String(location.location_code ?? location.code ?? ""),
    name: String(location.unit_name ?? location.name ?? ""),
    type: normalizeStorageType(location.storage_type ?? location.type),
    capacity: Number(location.capacity ?? 0),
    itemCount: Number(
      location.current_occupancy ??
        location.itemCount ??
        location.item_count ??
        0
    ),
    occupancyPercentage:
      location.occupancy_percentage !== undefined &&
      location.occupancy_percentage !== null
        ? Number(location.occupancy_percentage)
        : location.occupancyPercentage,
    locationPath: String(location.location_path ?? location.locationPath ?? ""),
    temperatureControlled: Boolean(
      location.is_climate_controlled ?? location.temperatureControlled
    ),
    restrictedAccess: Boolean(
      location.is_restricted_access ?? location.restrictedAccess
    ),
    description: String(location.description ?? ""),
    status: String(location.status ?? "Active"),
  };
}

function mapStorageLocationToApi(location: Partial<StorageLocation>) {
  const capacity = Number(location.capacity) || 0;
  const currentOccupancy = Number(location.itemCount) || 0;
  return {
    location_code: location.code,
    storage_type: location.type
      ? `${location.type.charAt(0).toUpperCase()}${location.type.slice(1)}`
      : undefined,
    unit_name: location.name,
    location_path: location.locationPath,
    capacity,
    current_occupancy: currentOccupancy,
    occupancy_percentage:
      capacity > 0 ? (currentOccupancy / capacity) * 100 : 0,
    is_climate_controlled: Boolean(location.temperatureControlled),
    is_restricted_access: Boolean(location.restrictedAccess),
    description: location.description || "",
    status: location.status || "Active",
  };
}

export async function getStorageLocations(): Promise<StorageLocation[]> {
  const all: StorageLocation[] = [];
  let page = 1;
  let hasNext = true;
  while (hasNext) {
    const payload = await requestStorageLocationsApi(`?page=${page}`);
    all.push(...extractStorageLocationList(payload).map(mapApiStorageLocation));
    hasNext = Boolean(payload?.links?.next ?? payload?.next);
    page += 1;
  }
  return all;
}

export async function createStorageLocation(
  data: Omit<StorageLocation, "id">
): Promise<StorageLocation> {
  const payload = await requestStorageLocationsApi("", {
    method: "POST",
    body: JSON.stringify(mapStorageLocationToApi(data)),
  });
  return mapApiStorageLocation(unwrapApiData(payload));
}

export async function updateStorageLocation(
  id: string,
  data: Partial<StorageLocation>
): Promise<StorageLocation> {
  const payload = await requestStorageLocationsApi(
    `${encodeURIComponent(id)}/`,
    {
      method: "PATCH",
      body: JSON.stringify(mapStorageLocationToApi(data)),
    }
  );
  return mapApiStorageLocation(unwrapApiData(payload));
}

export async function deleteStorageLocation(id: string): Promise<void> {
  await requestStorageLocationsApi(`${encodeURIComponent(id)}/`, {
    method: "DELETE",
  });
}

export async function getItemsInLocation(locationId: string) {
  const payload = await requestStorageLocationsApi(
    `${encodeURIComponent(locationId)}/audit/`
  );
  const audit = unwrapApiData(payload);
  const books = Array.isArray(audit.books)
    ? audit.books.map(mapApiBook)
    : extractBookList(audit);
  const manuscripts = Array.isArray(audit.manuscripts)
    ? audit.manuscripts.map(mapApiManuscript)
    : [];
  return {
    books,
    manuscripts,
  };
}

// ─── MEMBERS ──────────────────────────────────────────────────────────────────

const MEMBERS_PATH = "/jambushrusti/library/members/";

async function requestMembersApi(path: string, options: RequestInit = {}) {
  let accessToken = getLibraryAdminSession()?.accessToken;
  if (!API_BASE || !TENANT_ID) {
    throw new Error("The library members API is not configured.");
  }
  if (!accessToken) {
    throw new Error("Your session has expired. Please sign in again.");
  }

  const sendRequest = (token: string) =>
    fetch(`${API_BASE}${MEMBERS_PATH}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Tenant-ID": TENANT_ID,
        ...options.headers,
      },
    });

  let response = await sendRequest(accessToken);
  if (response.status === 401) {
    accessToken = await refreshLibraryAdminToken();
    response = await sendRequest(accessToken);
  }
  if (response.status === 204) {
    return undefined;
  }

  const payload = await response.json().catch(() => undefined);
  if (!response.ok) {
    throw new Error(
      getBookApiError(payload, `Member request failed (${response.status}).`)
    );
  }
  return payload;
}

function extractMemberList(payload: any): Record<string, any>[] {
  let value = payload;
  for (let depth = 0; depth < 5; depth += 1) {
    if (Array.isArray(value)) {
      return value;
    }
    if (!value || typeof value !== "object") {
      return [];
    }
    value =
      value.results ??
      value.data ??
      value.items ??
      value.members ??
      value.result;
  }
  return Array.isArray(value) ? value : [];
}

function normalizeMemberStatus(value: unknown): Member["status"] {
  const status = String(value || "pending").toLowerCase();
  if (status === "inactive") {
    return "suspended";
  }
  return ["active", "approved", "suspended", "pending"].includes(status)
    ? (status as Member["status"])
    : "pending";
}

function mapApiMember(member: Record<string, any>): Member {
  return {
    id: String(
      member.id ?? member.uuid ?? member.member_id ?? member.member_code ?? ""
    ),
    memberId: String(member.member_code ?? member.memberId ?? ""),
    name: String(member.full_name ?? member.name ?? ""),
    phone: String(member.phone_number ?? member.phone ?? ""),
    email: String(member.email ?? ""),
    address: String(member.street_address ?? member.address ?? ""),
    city: String(member.city ?? ""),
    state: String(member.state ?? ""),
    pincode: String(member.pincode ?? ""),
    avatarUrl: member.avatar_url ?? member.avatarUrl,
    referenceId: member.reference_member_id ?? member.referenceId ?? "",
    memberCategory: member.member_category ?? member.memberCategory ?? "",
    status: normalizeMemberStatus(member.status),
    booksIssued: Number(member.books_issued ?? member.booksIssued ?? 0),
    joinedDate: String(member.joining_date ?? member.joinedDate ?? ""),
    expiryDate: member.expiry_date ?? member.expiryDate ?? "",
  };
}

function mapMemberToApi(member: Partial<Member>) {
  return {
    member_code: member.memberId,
    full_name: member.name,
    phone_number: member.phone,
    email: member.email || "",
    member_category: member.memberCategory || "General",
    street_address: member.address || "",
    city: member.city || "",
    state: member.state || "",
    pincode: member.pincode || "",
    joining_date: member.joinedDate,
    expiry_date: member.expiryDate || null,
    reference_member_id: member.referenceId || null,
    status: member.status
      ? `${member.status.charAt(0).toUpperCase()}${member.status.slice(1)}`
      : undefined,
  };
}

export async function getMembers(): Promise<Member[]> {
  const all: Member[] = [];
  let page = 1;
  let hasNext = true;
  while (hasNext) {
    const payload = await requestMembersApi(`?page=${page}`);
    all.push(...extractMemberList(payload).map(mapApiMember));
    hasNext = Boolean(payload?.links?.next ?? payload?.next);
    page += 1;
  }
  return all;
}

export async function getMember(id: string): Promise<Member | undefined> {
  const payload = await requestMembersApi(`${encodeURIComponent(id)}/`);
  return mapApiMember(unwrapApiData(payload));
}

export async function createMember(
  data: Omit<Member, "id" | "booksIssued">
): Promise<Member> {
  const payload = await requestMembersApi("", {
    method: "POST",
    body: JSON.stringify(mapMemberToApi(data)),
  });
  return mapApiMember(unwrapApiData(payload));
}

export async function updateMember(
  id: string,
  data: Partial<Member>
): Promise<Member> {
  const payload = await requestMembersApi(`${encodeURIComponent(id)}/`, {
    method: "PATCH",
    body: JSON.stringify(mapMemberToApi(data)),
  });
  return mapApiMember(unwrapApiData(payload));
}

export async function deleteMember(id: string): Promise<void> {
  await requestMembersApi(`${encodeURIComponent(id)}/`, { method: "DELETE" });
}

// ─── BOOK ISSUES ──────────────────────────────────────────────────────────────

export async function getBookIssues(): Promise<BookIssue[]> {
  const all: BookIssue[] = [];
  let page = 1;
  let hasNext = true;
  while (hasNext) {
    const payload = await requestBookIssuesApi(`?page=${page}`);
    all.push(...extractBookIssueList(payload).map(mapApiBookIssue));
    hasNext = Boolean(payload?.links?.next ?? payload?.next);
    page += 1;
  }
  return all;
}

export async function getPendingReturns(): Promise<BookIssue[]> {
  const issues = await getBookIssues();
  return issues.filter(i => i.status !== "returned");
}

type BookIssuePayload = {
  issueNo: string;
  memberCode: string;
  memberName: string;
  memberMobile: string;
  bookName: string;
  bookId: string | number;
  issueDate: string;
  dueDate: string;
  remarks: string;
};

const BOOK_ISSUES_PATH = "/jambushrusti/library/book-issues/";

async function requestBookIssuesApi(
  path: string,
  options: RequestInit = {}
): Promise<any> {
  let accessToken = getLibraryAdminSession()?.accessToken;
  if (!API_BASE || !TENANT_ID) {
    throw new Error("The library book-issues API is not configured.");
  }
  if (!accessToken) {
    throw new Error("Your session has expired. Please sign in again.");
  }

  const requestUrl = path.startsWith("/")
    ? `${API_BASE}${path}`
    : `${API_BASE}${BOOK_ISSUES_PATH}${path}`;
  const sendRequest = (token: string) =>
    fetch(requestUrl, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Tenant-ID": TENANT_ID,
        ...options.headers,
      },
    });

  let response = await sendRequest(accessToken);
  if (response.status === 401) {
    accessToken = await refreshLibraryAdminToken();
    response = await sendRequest(accessToken);
  }
  if (response.status === 204) {
    return undefined;
  }

  const payload = await response.json().catch(() => undefined);
  if (!response.ok) {
    throw new Error(
      getBookApiError(
        payload,
        `Book issue request failed (${response.status}).`
      )
    );
  }
  return payload;
}

function extractBookIssueList(payload: any): Record<string, any>[] {
  let value = payload;
  for (let depth = 0; depth < 4; depth += 1) {
    if (Array.isArray(value)) {
      return value;
    }
    if (!value || typeof value !== "object") {
      return [];
    }
    value = value.results ?? value.data ?? value.items ?? value.book_issues;
  }
  return Array.isArray(value) ? value : [];
}

function mapApiBookIssue(issue: Record<string, any>): BookIssue {
  const book = issue.book && typeof issue.book === "object" ? issue.book : {};
  const normalizeDate = (value: unknown) => {
    const date = String(value ?? "");
    const match = date.match(/^(\d{2})-(\d{2})-(\d{4})$/);
    return match ? `${match[3]}-${match[2]}-${match[1]}` : date;
  };
  const dueDate = normalizeDate(issue.due_date ?? issue.dueDate);
  const suppliedStatus = String(issue.status ?? "").toLowerCase();
  const status = ["active", "overdue", "returned"].includes(suppliedStatus)
    ? suppliedStatus
    : dueDate && dueDate < new Date().toISOString().split("T")[0]
      ? "overdue"
      : "active";

  return {
    id: String(issue.id ?? issue.uuid ?? issue.issue_no ?? ""),
    issueNo: String(issue.issue_no ?? issue.issueNo ?? ""),
    memberId: String(issue.member_code ?? issue.memberId ?? ""),
    memberName: String(issue.member_name ?? issue.memberName ?? ""),
    memberMobile: String(issue.member_mobile ?? issue.memberMobile ?? ""),
    bookId: String(book.id ?? issue.book ?? issue.book_id ?? ""),
    bookNo: String(issue.book_no ?? book.book_no ?? issue.book ?? ""),
    bookTitle: String(
      issue.book_name ?? book.main_title ?? issue.bookTitle ?? ""
    ),
    issueDate: normalizeDate(issue.issue_date ?? issue.issueDate),
    dueDate,
    conditionAtIssue: String(issue.condition_at_issue ?? "Good"),
    status,
    daysRemaining: dueDate
      ? Math.ceil((new Date(dueDate).getTime() - Date.now()) / 86400000)
      : undefined,
    remarks: String(issue.remarks ?? ""),
  };
}

function mapBookIssueToApi(data: BookIssuePayload) {
  return {
    issue_no: data.issueNo,
    member_code: data.memberCode,
    member_name: data.memberName,
    member_mobile: data.memberMobile,
    book_name: data.bookName,
    book: data.bookId,
    issue_date: data.issueDate,
    due_date: data.dueDate,
    remarks: data.remarks,
  };
}

export async function issueBook(data: BookIssuePayload): Promise<BookIssue> {
  const payload = await requestBookIssuesApi("", {
    method: "POST",
    body: JSON.stringify(mapBookIssueToApi(data)),
  });
  return mapApiBookIssue(unwrapApiData(payload));
}

export async function updateBookIssue(
  id: string,
  data: BookIssuePayload
): Promise<BookIssue> {
  const payload = await requestBookIssuesApi(`${encodeURIComponent(id)}/`, {
    method: "PATCH",
    body: JSON.stringify(mapBookIssueToApi(data)),
  });
  return mapApiBookIssue(unwrapApiData(payload));
}

export async function deleteBookIssue(id: string): Promise<void> {
  await requestBookIssuesApi(`${encodeURIComponent(id)}/`, {
    method: "DELETE",
  });
}

export type ProcessBookReturnPayload = {
  memberCode: string;
  bookNo: string;
  conditionOnReturn: string;
  returnDate: string;
  fineAmount: number;
  remarks: string;
};

export type BookReturnRecord = {
  id: string;
  returnNo: string;
  memberCode: string;
  memberName: string;
  bookNo: string;
  bookName: string;
  returnDate: string;
  conditionOnReturn: string;
  fineAmount: number;
  status: string;
  remarks?: string;
};

function mapApiBookReturn(item: Record<string, any>): BookReturnRecord {
  return {
    id: String(item.id ?? item.uuid ?? item.return_no ?? ""),
    returnNo: String(item.return_no ?? ""),
    memberCode: String(item.member_code ?? ""),
    memberName: String(item.member_name ?? ""),
    bookNo: String(item.book_no ?? ""),
    bookName: String(item.book_name ?? ""),
    returnDate: String(item.return_date ?? ""),
    conditionOnReturn: String(item.condition_on_return ?? ""),
    fineAmount: Number(item.fine_amount ?? 0),
    status: String(item.status ?? ""),
    remarks: item.remarks,
  };
}

export async function getBookReturns(): Promise<BookReturnRecord[]> {
  const all: BookReturnRecord[] = [];
  let page = 1;
  let hasNext = true;
  while (hasNext) {
    const payload = await requestBookIssuesApi(
      `/jambushrusti/library/book-returns/?page=${page}`
    );
    let value = payload;
    for (let depth = 0; depth < 4; depth += 1) {
      if (Array.isArray(value)) {
        all.push(...value.map(mapApiBookReturn));
        break;
      }
      if (!value || typeof value !== "object") {
        break;
      }
      value = value.results ?? value.data ?? value.items ?? value.book_returns;
    }
    hasNext = Boolean(payload?.links?.next ?? payload?.next);
    page += 1;
  }
  return all;
}

export async function receiveBook(
  data: ProcessBookReturnPayload
): Promise<void> {
  await requestBookIssuesApi(
    "/jambushrusti/library/book-returns/process-return/",
    {
      method: "POST",
      body: JSON.stringify({
        member_code: data.memberCode,
        book_no: data.bookNo,
        condition_on_return: data.conditionOnReturn,
        return_date: data.returnDate,
        fine_amount: data.fineAmount,
        remarks: data.remarks,
      }),
    }
  );
}

// ─── APPROVALS ────────────────────────────────────────────────────────────────

export async function getApprovals(): Promise<Approval[]> {
  await delay();
  return seedApprovals;
}

export async function createApproval(
  data: Omit<Approval, "id" | "requestedOn" | "status">
): Promise<Approval> {
  await delay(300);
  const newApproval: Approval = {
    ...data,
    id: `ap${Date.now()}`,
    requestedOn: new Date().toISOString().split("T")[0],
    status: "pending",
  };
  seedApprovals.unshift(newApproval);
  return newApproval;
}

export async function updateApprovalStatus(
  id: string,
  status: "approved" | "rejected"
): Promise<void> {
  await delay(300);
  const ap = seedApprovals.find(a => a.id === id);
  if (ap) {
    ap.status = status;
  }
}

// ─── TICKETS ──────────────────────────────────────────────────────────────────

export async function getTickets(): Promise<Ticket[]> {
  await delay();
  return seedTickets;
}

export async function getTicket(id: string): Promise<Ticket | undefined> {
  await delay(150);
  return seedTickets.find(t => t.id === id);
}

export async function createTicket(
  data: Omit<Ticket, "id" | "ticketNo" | "createdAt" | "updatedAt" | "messages">
): Promise<Ticket> {
  await delay(400);
  const newTicket: Ticket = {
    ...data,
    id: `t${Date.now()}`,
    ticketNo: `TKT-${String(seedTickets.length + 1).padStart(3, "0")}`,
    createdAt: new Date().toISOString(),
    updatedAt: new Date().toISOString(),
    messages: [],
  };
  seedTickets.unshift(newTicket);
  return newTicket;
}

export async function addTicketMessage(
  ticketId: string,
  messageText: string,
  author = "Librarian"
): Promise<Ticket> {
  await delay(300);
  const ticket = seedTickets.find(t => t.id === ticketId);
  if (ticket) {
    const newMessage = {
      id: `tm${Date.now()}`,
      author,
      role: "librarian" as const,
      message: messageText,
      timestamp: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
    };
    ticket.messages.push(newMessage);
    ticket.updatedAt = new Date().toISOString();
    return ticket;
  }
  throw new Error("Ticket not found");
}

export async function updateTicketStatus(
  ticketId: string,
  status: Ticket["status"]
): Promise<Ticket> {
  await delay(300);
  const ticket = seedTickets.find(t => t.id === ticketId);
  if (ticket) {
    ticket.status = status;
    ticket.updatedAt = new Date().toISOString();
    return ticket;
  }
  throw new Error("Ticket not found");
}

// ─── SETTINGS ─────────────────────────────────────────────────────────────────

export async function getSettings(): Promise<LibrarySettings> {
  await delay();
  return seedSettings;
}

export async function updateSettings(
  data: Partial<LibrarySettings>
): Promise<LibrarySettings> {
  await delay(400);
  Object.assign(seedSettings, data);
  return seedSettings;
}

// ─── FINES MANAGEMENT ─────────────────────────────────────────────────────────

const PAYMENTS_PATH = "/jambushrusti/library/payments/";

async function requestPaymentsApi(path: string, options: RequestInit = {}) {
  let accessToken = getLibraryAdminSession()?.accessToken;
  if (!API_BASE || !TENANT_ID) {
    throw new Error("The library payments API is not configured.");
  }
  if (!accessToken) {
    throw new Error("Your session has expired. Please sign in again.");
  }

  const sendRequest = (token: string) =>
    fetch(`${API_BASE}${PAYMENTS_PATH}${path}`, {
      ...options,
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
        "X-Tenant-ID": TENANT_ID,
        ...options.headers,
      },
    });

  let response = await sendRequest(accessToken);
  if (response.status === 401) {
    accessToken = await refreshLibraryAdminToken();
    response = await sendRequest(accessToken);
  }
  if (response.status === 204) {
    return undefined;
  }

  const payload = await response.json().catch(() => undefined);
  if (!response.ok) {
    throw new Error(
      getBookApiError(payload, `Payment request failed (${response.status}).`)
    );
  }
  return payload;
}

function extractPaymentList(payload: any): Record<string, any>[] {
  let value = payload;
  for (let depth = 0; depth < 5; depth += 1) {
    if (Array.isArray(value)) {
      return value;
    }
    if (!value || typeof value !== "object") {
      return [];
    }
    value =
      value.results ??
      value.data ??
      value.items ??
      value.payments ??
      value.result;
  }
  return Array.isArray(value) ? value : [];
}

const paymentCategoryToUi: Record<string, FineItem["category"]> = {
  "late return": "late_return",
  "lost book": "lost_book",
  "damaged book": "damaged_book",
  "membership expiry": "membership_expiry",
};

const paymentCategoryToApi: Record<FineItem["category"], string> = {
  late_return: "Late Return",
  lost_book: "Lost Book",
  damaged_book: "Damaged Book",
  membership_expiry: "Membership Expiry",
};

function mapApiPayment(payment: Record<string, any>): FineItem {
  const categoryKey = String(payment.category || "Late Return").toLowerCase();
  const status = String(payment.status || "Pending").toLowerCase();
  return {
    id: String(
      payment.id ?? payment.uuid ?? payment.payment_id ?? payment.fine_no ?? ""
    ),
    fineNo: String(
      payment.payment_no ?? payment.fine_no ?? payment.fineNo ?? ""
    ),
    category: paymentCategoryToUi[categoryKey] || "late_return",
    memberId: String(payment.member_code ?? payment.memberId ?? ""),
    memberName: String(payment.member_name ?? payment.memberName ?? ""),
    phone: String(payment.phone_number ?? payment.phone ?? ""),
    bookNo: payment.book_no ?? payment.bookNo ?? "",
    bookTitle: payment.book_title ?? payment.bookTitle ?? "",
    dueDate: payment.due_date ?? payment.dueDate,
    returnDate: payment.return_date ?? payment.returnDate,
    daysLate: Number(payment.days_late ?? payment.daysLate ?? 0),
    damageSeverity: payment.damage_severity ?? payment.damageSeverity,
    expiryDate: payment.expiry_date ?? payment.expiryDate,
    amount: Number(payment.amount ?? 0),
    status: ["pending", "paid", "waived"].includes(status)
      ? (status as FineItem["status"])
      : "pending",
    createdDate: String(payment.created_date ?? payment.created_at ?? ""),
    paidDate: payment.paid_date ?? payment.paidDate,
    remarks: payment.remarks ?? "",
  };
}

function mapPaymentToApi(payment: Partial<FineItem>) {
  return {
    category: payment.category
      ? paymentCategoryToApi[payment.category]
      : undefined,
    amount: payment.amount,
    member_name: payment.memberName,
    member_code: payment.memberId,
    phone_number: payment.phone,
    book_title: payment.bookTitle || "",
    book_no: payment.bookNo || "",
    remarks: payment.remarks || "",
    status: payment.status
      ? `${payment.status.charAt(0).toUpperCase()}${payment.status.slice(1)}`
      : undefined,
  };
}

export async function getFines(): Promise<FineItem[]> {
  const all: FineItem[] = [];
  let page = 1;
  let hasNext = true;
  while (hasNext) {
    const payload = await requestPaymentsApi(`?page=${page}`);
    all.push(...extractPaymentList(payload).map(mapApiPayment));
    hasNext = Boolean(payload?.links?.next ?? payload?.next);
    page += 1;
  }
  return all;
}

export async function createFine(
  data: Omit<FineItem, "id" | "fineNo" | "createdDate">
): Promise<FineItem> {
  const payload = await requestPaymentsApi("", {
    method: "POST",
    body: JSON.stringify(mapPaymentToApi(data)),
  });
  return mapApiPayment(unwrapApiData(payload));
}

export async function payFine(
  fineId: string,
  paymentMethod = "Cash"
): Promise<FineItem> {
  const payload = await requestPaymentsApi(`${encodeURIComponent(fineId)}/`, {
    method: "PATCH",
    body: JSON.stringify({
      status: "Paid",
      remarks: `Paid via ${paymentMethod}`,
    }),
  });
  return mapApiPayment(unwrapApiData(payload));
}

export async function waiveFine(
  fineId: string,
  reason = "Waived by librarian"
): Promise<FineItem> {
  const payload = await requestPaymentsApi(`${encodeURIComponent(fineId)}/`, {
    method: "PATCH",
    body: JSON.stringify({ status: "Waived", remarks: reason }),
  });
  return mapApiPayment(unwrapApiData(payload));
}

export async function deleteFine(fineId: string): Promise<void> {
  await requestPaymentsApi(`${encodeURIComponent(fineId)}/`, {
    method: "DELETE",
  });
}
